import { RotateCw } from 'lucide-react';
import { ReviewCard as ReviewCardModel } from '../../../sdk/@types/Revisao';
import Quizz from '../Quizz/Quizz';
import CardMarkdown from '../Revisao/CardMarkdown';
import { GlassCard } from '../Revisao/GlassCard';
import OclusaoFace from '../Revisao/OclusaoFace';
import { leituraOclusao } from '../Revisao/oclusaoEstudo';
import { REGIAO_INTERATIVA } from './useCardGestures';
import { cardTipoMostrado } from '../Revisao/cardTipo';

export interface ReviewCardProps {
  card: ReviewCardModel;
  flipped: boolean;
  onToggleFlip?: () => void;
  resolveImageUrl?: (fileKey: string) => string;
  subjectName?: string;
  /** Deck chip. Falls back to the first tag, then subjectName. */
  deckName?: string;
  /** Header slot for the admin "Editar" entry (PilulaEditar). Omit it and nothing renders. */
  editSlot?: React.ReactNode;
  /** Footer slot, rendered inside the card surface after the body. Omit it and nothing renders. */
  ratingSlot?: React.ReactNode;
  /** Opens the verso for the asked marking. Does not record a rating. */
  onRevelar?: () => void;
}

type CardKind = 'question' | 'occlusion' | 'text';

interface CardRegions {
  deckLabel: string;
  faceKey: 'frente' | 'verso';
  faceText: 'Frente' | 'Verso';
  /** Eyebrow text and `data-card-tipo`. */
  tipo: string;
  kind: CardKind;
  apoio: string;
  flipHint: string;
  shortIdLabel: string | null;
}

/**
 * Deck label, face name, eyebrow, apoio, flip hint and short id.
 * Derived once for every layout; only the body content branches.
 */
function cardRegions(
  card: ReviewCardModel,
  flipped: boolean,
  deckName: string | undefined,
  subjectName: string | undefined,
  leitura: ReturnType<typeof leituraOclusao>,
): CardRegions {
  const deckLabel = deckName ?? card.tags?.[0] ?? subjectName ?? '';
  const faceKey = flipped ? 'verso' : 'frente';
  const faceText = flipped ? 'Verso' : 'Frente';

  let kind: CardKind = 'text';
  let tipo = 'Questão';
  if (card.source === 'question') {
    kind = 'question';
  } else {
    tipo = cardTipoMostrado(card);
    kind = tipo === 'Oclusão' ? 'occlusion' : 'text';
  }

  let apoio = '';
  if (kind === 'question') {
    const agency = card.question?.testingAgency;
    apoio = [agency?.name, agency?.year].filter(Boolean).join(' · ');
  } else if (kind === 'occlusion') {
    if (!leitura.rotuloMarcacao) {
      const count = card.masks?.length ?? 0;
      if (count === 1) apoio = '1 marcação';
      else if (count > 1) apoio = `${count} marcações`;
    }
  } else if (subjectName && subjectName !== deckLabel) {
    apoio = subjectName;
  }

  let flipHint = 'Toque para virar';
  if (kind === 'occlusion' && !flipped) {
    flipHint =
      leitura.askedIndex !== null
        ? 'Revelar a marcação'
        : 'Toque nas marcações ou vire o cartão';
  } else if (flipped) flipHint = 'Toque para voltar à frente';

  const shortIdLabel =
    typeof card.shortId === 'number' ? `#${String(card.shortId).padStart(4, '0')}` : null;

  return { deckLabel, faceKey, faceText, tipo, kind, apoio, flipHint, shortIdLabel };
}

/** One three-region review card on GlassCard. `onRevelar` opens the answer side. It does not flip the card and does not record a rating. */
export default function ReviewCard({
  card,
  flipped,
  onToggleFlip,
  resolveImageUrl,
  subjectName,
  deckName,
  editSlot,
  ratingSlot,
  onRevelar,
}: ReviewCardProps) {
  const leitura = leituraOclusao(card);
  const regions = cardRegions(card, flipped, deckName, subjectName, leitura);

  const face = flipped ? card.back : card.front;
  // A missing back repeats the front (one-sided card); an empty string is a blank verso (the composer draft).
  const faceText = flipped && (card.back === null || card.back === undefined) ? card.front : face;

  return (
    <div
      data-review-card
      onClick={(event) => {
        const alvo = event.target;
        if (alvo instanceof Element && alvo.closest(REGIAO_INTERATIVA)) return;
        onToggleFlip?.();
      }}
      className="cursor-pointer select-none w-full"
    >
      <GlassCard className="w-full">
        <div className="flex min-h-[20rem] w-full flex-col gap-4">
          <header data-review-card-header className="flex items-center gap-2 min-w-0">
            {regions.deckLabel ? (
              <span
                data-deck-chip
                className="min-w-0 truncate rounded-chip bg-surface-muted px-2.5 py-0.5 text-xs font-medium text-ink-secondary"
              >
                {regions.deckLabel}
              </span>
            ) : null}
            <span
              data-face={regions.faceKey}
              className="shrink-0 text-[0.65rem] font-semibold uppercase tracking-wider text-ink-muted"
            >
              {regions.faceText}
            </span>
            <div
              data-edit-slot
              data-nogesture
              className="ml-auto flex min-h-8 min-w-8 shrink-0 items-center justify-end"
              onClick={(event) => event.stopPropagation()}
            >
              {editSlot}
            </div>
          </header>

          <div data-card-body className="flex flex-1 flex-col items-center gap-3 min-w-0">
            <span
              data-eyebrow
              data-card-tipo={regions.tipo}
              className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
            >
              {regions.tipo}
            </span>

            {regions.kind === 'question' ? (
              <div className="w-full min-w-0">
                <Quizz
                  questionReceived={{
                    question: card.question,
                    answer: flipped
                      ? card.question?.choices?.find((choice) => choice.correct)
                      : undefined,
                  }}
                  showResolution={flipped}
                />
              </div>
            ) : null}

            {regions.kind === 'text' ? (
              <div className="w-full min-w-0 max-w-xl text-center text-xl font-medium leading-relaxed text-ink md:text-2xl">
                <CardMarkdown value={faceText ?? ''} resolveImageUrl={resolveImageUrl} />
              </div>
            ) : null}

            {regions.kind === 'occlusion' ? (
              <OclusaoFace
                masks={card.masks ?? []}
                frontImageKey={card.frontImageKey}
                resolveImageUrl={resolveImageUrl}
                face={flipped ? 'back' : 'front'}
                mode={leitura.mode}
                askedIndex={leitura.askedIndex}
                explicacao={card.back}
                onRevelar={flipped ? undefined : onRevelar}
              />
            ) : null}

            {regions.apoio ? (
              <p data-apoio className="max-w-xl text-center text-sm text-ink-secondary">
                {regions.apoio}
              </p>
            ) : null}
          </div>

          <footer
            data-review-card-footer
            className="mt-auto flex flex-wrap items-center justify-between gap-2"
          >
            <div data-flip-hint className="flex items-center gap-1.5">
              <RotateCw size={14} className="text-ink-muted" />
              <span className="text-xs font-medium text-ink-muted">{regions.flipHint}</span>
            </div>
            {regions.shortIdLabel ? (
              <span data-short-id className="shrink-0 font-mono text-xs text-ink-secondary">
                {regions.shortIdLabel}
              </span>
            ) : null}
          </footer>

          {ratingSlot ? (
            <div
              data-rating-slot
              data-nogesture
              onClick={(event) => event.stopPropagation()}
            >
              {ratingSlot}
            </div>
          ) : null}
        </div>
      </GlassCard>
    </div>
  );
}
